import { useEffect, useRef, useState } from "react"
import { NavLink, useNavigate } from "react-router"
import {
  LayoutDashboard,
  Server,
  Users,
  UserCircle,
  ScrollText,
  Shield,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
  LogOut,
  type LucideIcon,
} from "lucide-react"
import { useAuth } from "../auth/AuthProvider.js"
import { useTheme } from "../theme/ThemeProvider.js"
import { useClickOutside } from "../hooks/useClickOutside.js"

const SIDEBAR_COLLAPSED_KEY = "mc-sidebar-collapsed"

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/servers", label: "Mis Servidores", icon: Server },
  { to: "/families", label: "Familias", icon: Users },
  { to: "/profile", label: "Perfil", icon: UserCircle },
]

/** FR section 16: top-level nav. /admin y Auditoría solo para host_owner (audit no-host_owner requiere elegir servidor, ver AuditLogPage). */
export function useNavItems(): NavItem[] {
  const { user } = useAuth()
  return user?.systemRole === "host_owner"
    ? [...NAV_ITEMS, { to: "/audit", label: "Auditoría", icon: ScrollText }, { to: "/admin", label: "Admin", icon: Shield }]
    : NAV_ITEMS
}

function readCollapsedPreference(): boolean {
  if (typeof window === "undefined") return false
  return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1"
}

export function Sidebar() {
  const items = useNavItems()
  const [collapsed, setCollapsed] = useState(readCollapsedPreference)

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0")
  }, [collapsed])

  return (
    <nav
      className="mc-shell__sidebar"
      aria-label="Navegación principal"
      style={{
        flexDirection: "column",
        padding: "var(--space-4)",
        borderRight: "1px solid var(--border-default)",
        background: "var(--bg-surface)",
        width: collapsed ? 72 : 240,
        transition: "width var(--duration-small) var(--ease-out)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          marginBottom: "var(--space-4)",
        }}
      >
        {!collapsed && <strong style={{ fontSize: "var(--text-lg)", whiteSpace: "nowrap" }}>minecraft-infra</strong>}
        <button
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expandir barra lateral" : "Colapsar barra lateral"}
          title={collapsed ? "Expandir" : "Colapsar"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            color: "var(--text-muted)",
            width: 32,
            height: 32,
            borderRadius: "var(--radius-sm)",
            cursor: "pointer",
          }}
        >
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: 1 }}>
        {items.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className="mc-navlink"
              title={collapsed ? item.label : undefined}
              style={collapsed ? { justifyContent: "center" } : undefined}
            >
              <Icon size={18} aria-hidden />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          )
        })}
      </div>

      <SidebarUserFooter collapsed={collapsed} />
    </nav>
  )
}

function SidebarUserFooter({ collapsed }: { collapsed: boolean }) {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const footerRef = useRef<HTMLDivElement>(null)

  useClickOutside(footerRef, () => setMenuOpen(false), menuOpen)

  // AppShell solo renderiza detrás de RequireAuth, pero este bloque se
  // escribe defensivamente por si algún día deja de ser así.
  if (!user) return null

  const name = user.displayName ?? user.username

  const handleLogout = async () => {
    setLoggingOut(true)
    await logout()
    navigate("/login")
  }

  return (
    <div
      ref={footerRef}
      style={{
        position: "relative",
        borderTop: "1px solid var(--border-default)",
        marginTop: "var(--space-2)",
        paddingTop: "var(--space-2)",
      }}
    >
      <button
        onClick={() => setMenuOpen((o) => !o)}
        aria-label={`Menú de usuario, ${name}`}
        aria-expanded={menuOpen}
        title={collapsed ? name : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          justifyContent: collapsed ? "center" : "flex-start",
          width: "100%",
          padding: "var(--space-2)",
          background: "transparent",
          border: "none",
          borderRadius: "var(--radius-sm)",
          cursor: "pointer",
          color: "var(--text-primary)",
        }}
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt=""
            width={24}
            height={24}
            style={{ borderRadius: "var(--radius-full)", objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <UserCircle size={24} aria-hidden style={{ flexShrink: 0 }} />
        )}
        {!collapsed && (
          <span style={{ fontSize: "var(--text-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </span>
        )}
      </button>

      {menuOpen && (
        <div
          role="menu"
          style={{
            position: "absolute",
            bottom: "calc(100% + var(--space-2))",
            left: collapsed ? 0 : undefined,
            minWidth: 200,
            background: "var(--bg-surface-raised)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-md)",
            padding: "var(--space-1)",
            display: "flex",
            flexDirection: "column",
            zIndex: 50,
          }}
        >
          <NavLink
            to="/profile"
            role="menuitem"
            className="mc-navlink"
            onClick={() => setMenuOpen(false)}
          >
            <UserCircle size={16} aria-hidden />
            <span>Perfil</span>
          </NavLink>
          <button
            role="menuitem"
            onClick={() => {
              toggleTheme()
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              padding: "var(--space-2) var(--space-3)",
              background: "transparent",
              border: "none",
              borderRadius: "var(--radius-sm)",
              color: "var(--text-muted)",
              fontSize: "var(--text-sm)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            {theme === "dark" ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
            <span>{theme === "dark" ? "Tema claro" : "Tema oscuro"}</span>
          </button>
          <button
            role="menuitem"
            disabled={loggingOut}
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              padding: "var(--space-2) var(--space-3)",
              background: "transparent",
              border: "none",
              borderRadius: "var(--radius-sm)",
              color: "var(--status-error)",
              fontSize: "var(--text-sm)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <LogOut size={16} aria-hidden />
            <span>{loggingOut ? "Cerrando sesión…" : "Cerrar sesión"}</span>
          </button>
        </div>
      )}
    </div>
  )
}

export function BottomNav() {
  const items = useNavItems()

  return (
    <nav className="mc-shell__bottomnav" aria-label="Navegación principal">
      {items.map((item) => {
        const Icon = item.icon
        return (
          <NavLink key={item.to} to={item.to} className="mc-bottomnav-link">
            <Icon size={20} aria-hidden />
            <span>{item.label}</span>
          </NavLink>
        )
      })}
    </nav>
  )
}
